import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Cookies from "js-cookie";
import type { EhrClinician } from "./members.service";

/** Onboarding portal (implementation portal) API. */

export type StepOwner = "customer" | "nudge";

export type StepStatus = "locked" | "awaiting_customer" | "in_progress_nudge" | "blocked" | "complete";

export type StepKey = "baa" | "group_admin" | "ehr_login" | "workflow" | "templates" | "ai_assistant" | "go_live";

export type SetupStep = {
  key: StepKey;
  title: string;
  owner: StepOwner;
  status: StepStatus;
  status_detail: string;
  completed_at: string | null;
};

export type SetupBanner = {
  kind: "action_needed" | "nudge_working" | "complete";
  headline: string;
  body: string;
  step_key: StepKey | null;
};

export type WorkflowQuestion = {
  id: string;
  group: string;
  prompt: string;
  type: "multi" | "single" | "text" | "overrides";
  required: boolean;
  options: { value: string; label: string; exclusive: boolean; other: boolean }[];
  show_if: { question: string; includes?: string; min_selected?: number } | null;
};

/** How one visit type differs from the practice's usual flow. */
export type WorkflowOverride = { visit_type: string; B1?: string | null; B2?: string[]; B2_other?: string };

export type SetupWorkflow = {
  /** Served by the API, so a new question needs no frontend change. */
  questions: WorkflowQuestion[];
  answers: Record<string, unknown>;
  visit_type_overrides: WorkflowOverride[];
  submitted_at: string | null;
  headline: string;
  summary: { question: string; answer: string }[];
  /** False once the practice is live. */
  editable: boolean;
};

export type SetupPortal = {
  group_name: string;
  viewer_email: string;
  steps: SetupStep[];
  progress: {
    completed: number;
    total: number;
    current_position: number;
    current_step_key: StepKey | null;
    is_complete: boolean;
  };
  banner: SetupBanner;
  integration: {
    email: string | null;
    phone: string | null;
  };
  baa: {
    signed_at: string | null;
    signer_name: string;
    signer_email: string;
    sent_at: string | null;
    customer_signed_at: string | null;
    countersigner_name: string;
    /** The agreement was exchanged and signed by email, not through DocuSign. */
    signed_offline: boolean;
    /** Whether the customer may mark it signed offline (off once e-sign is live). */
    offline_allowed: boolean;
    suggested_email: string;
    cc_emails?: string[];
  };
  group_admin: { submitted: boolean; invited_at: string | null };
  ehr:
    | { submitted: false }
    | {
        submitted: true;
        verification_status: "pending" | "verified" | "failed";
        verification_detail: string;
        submitted_at: string | null;
      };
  /** Null on a portal created before the step existed. */
  workflow: SetupWorkflow | null;
  go_live: {
    providers: EhrClinician[];
    pulled_at: string | null;
    has_directory: boolean;
    /** Inviting opens once the EHR login verifies; "we're live" waits on steps 5 and 6. */
    can_complete: boolean;
  };
};

/** What an unverified visitor gets back. */
export type SetupGate = {
  group_name: string;
  requires_verification: true;
};

export type SetupResponse = SetupPortal | SetupGate;

export const isGated = (data: SetupResponse | undefined): data is SetupGate =>
  !!data && (data as SetupGate).requires_verification === true;

let portalCsrfToken = "";

/** Fetch the CSRF token for this session; resolves once the cookie and token are set. */
export async function primePortalCsrf(): Promise<void> {
  try {
    const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/users/csrf/`, { credentials: "include" });
    const data = (await res.json()) as { token?: string };
    if (data?.token) portalCsrfToken = data.token;
  } catch {
    // A submit surfaces its own error; don't block the page from rendering.
  }
}

/** Every write returns the whole portal, so cache it instead of refetching it. */
async function cachePortal(
  { token }: { token: string },
  {
    dispatch,
    queryFulfilled,
  }: { dispatch: (action: unknown) => unknown; queryFulfilled: Promise<{ data: SetupPortal }> },
) {
  try {
    const { data } = await queryFulfilled;
    dispatch(onboardingApi.util.upsertQueryData("getSetupPortal", token, data));
  } catch {
    // The caller surfaces the error.
  }
}

export const onboardingApi = createApi({
  reducerPath: "onboardingApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_URL,
    // The portal session is an HttpOnly cookie, so every call carries credentials.
    credentials: "include",
    prepareHeaders: (headers, { type }) => {
      if (type === "mutation") {
        // From /users/csrf/, not Cookies.get: a parent-domain csrftoken (prod + dev) would win.
        const csrf = portalCsrfToken || Cookies.get("csrftoken");
        if (csrf) headers.set("X-CSRFToken", csrf);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getSetupPortal: builder.query<SetupResponse, string>({
      query: (token) => `/onboarding/portal/${token}/`,
    }),

    requestSetupAccess: builder.mutation<{ detail: string }, { token: string; email: string }>({
      query: ({ token, email }) => ({
        url: `/onboarding/portal/${token}/access/`,
        method: "POST",
        body: { email },
      }),
    }),

    verifySetupAccess: builder.mutation<SetupPortal, { token: string; accessToken: string }>({
      query: ({ token, accessToken }) => ({
        url: `/onboarding/portal/${token}/access/verify/`,
        method: "POST",
        body: { access_token: accessToken },
      }),
      onQueryStarted: cachePortal,
    }),

    submitGroupAdmin: builder.mutation<
      SetupPortal,
      { token: string; name: string; email: string; phone?: string; password: string }
    >({
      query: ({ token, ...body }) => ({
        url: `/onboarding/portal/${token}/steps/group-admin/`,
        method: "POST",
        body,
      }),
      onQueryStarted: cachePortal,
    }),

    /** Names who the BAA is issued to and sends it. */
    setBaaRecipient: builder.mutation<
      SetupPortal,
      { token: string; email: string; name?: string; cc_emails?: string[] }
    >({
      query: ({ token, ...body }) => ({
        url: `/onboarding/portal/${token}/steps/baa/recipient/`,
        method: "POST",
        body,
      }),
      onQueryStarted: cachePortal,
    }),

    /** Re-delivers the DocuSign email — signing mail lands in spam often enough to need it. */
    resendBaa: builder.mutation<SetupPortal, { token: string }>({
      query: ({ token }) => ({
        url: `/onboarding/portal/${token}/steps/baa/resend/`,
        method: "POST",
      }),
      onQueryStarted: cachePortal,
    }),

    /** Asks DocuSign directly where the envelope stands — the customer's "I've signed it". */
    refreshBaaStatus: builder.mutation<SetupPortal, { token: string }>({
      query: ({ token }) => ({
        url: `/onboarding/portal/${token}/steps/baa/refresh/`,
        method: "POST",
      }),
      onQueryStarted: cachePortal,
    }),

    /** Step 1 without DocuSign: the customer confirms the BAA was signed by email. */
    markBaaSignedOffline: builder.mutation<SetupPortal, { token: string }>({
      query: ({ token }) => ({
        url: `/onboarding/portal/${token}/steps/baa/offline/`,
        method: "POST",
      }),
      onQueryStarted: cachePortal,
    }),

    submitEhrLogin: builder.mutation<
      SetupPortal,
      {
        token: string;
        ehr_name: string;
        login_url?: string;
        username: string;
        password: string;
        auth_notes?: string;
        /** Up to 5 template names; blanks are dropped server-side. */
        priority_templates?: (string | undefined)[];
      }
    >({
      query: ({ token, ...body }) => ({
        url: `/onboarding/portal/${token}/steps/ehr-login/`,
        method: "POST",
        body,
      }),
      onQueryStarted: cachePortal,
    }),

    /** Autosaves a draft; `submit` validates the required questions and completes the step. */
    saveWorkflow: builder.mutation<
      SetupPortal,
      { token: string; answers: Record<string, unknown>; visit_type_overrides: WorkflowOverride[]; submit: boolean }
    >({
      query: ({ token, ...body }) => ({
        url: `/onboarding/portal/${token}/steps/workflow/`,
        method: "POST",
        body,
      }),
      onQueryStarted: (arg, api) => {
        // Optimistic: a draft must survive the step being collapsed while its save is in flight.
        api.dispatch(
          onboardingApi.util.updateQueryData("getSetupPortal", arg.token, (portal) => {
            if (!isGated(portal) && portal.workflow) {
              portal.workflow.answers = arg.answers;
              portal.workflow.visit_type_overrides = arg.visit_type_overrides;
            }
          }),
        );
        return cachePortal(arg, api);
      },
    }),

    inviteProvider: builder.mutation<
      SetupPortal & { invite_result?: { email: string; state: EhrClinician["invite_state"] } },
      { token: string; email: string; name?: string; ehr_provider_id?: string }
    >({
      query: ({ token, ...body }) => ({
        url: `/onboarding/portal/${token}/steps/go-live/invite/`,
        method: "POST",
        body,
      }),
      onQueryStarted: cachePortal,
    }),

    completeGoLive: builder.mutation<SetupPortal, { token: string }>({
      query: ({ token }) => ({
        url: `/onboarding/portal/${token}/steps/go-live/complete/`,
        method: "POST",
      }),
      onQueryStarted: cachePortal,
    }),
  }),
});

export const {
  useGetSetupPortalQuery,
  useRequestSetupAccessMutation,
  useVerifySetupAccessMutation,
  useSubmitGroupAdminMutation,
  useSubmitEhrLoginMutation,
  useSaveWorkflowMutation,
  useSetBaaRecipientMutation,
  useResendBaaMutation,
  useRefreshBaaStatusMutation,
  useMarkBaaSignedOfflineMutation,
  useInviteProviderMutation,
  useCompleteGoLiveMutation,
} = onboardingApi;

/** Used by the in-app banner for logged-in group admins. */
